// src/pages/Settings.jsx
// Phase A2 additions in Security tab:
//   - "Install KaiLux" PWA install button (shown when browser supports it)
//   - "Installed" confirmation badge when already installed
//   - Sign-out clears offline snapshots via clearOfflineSnapshots()
// All other tabs unchanged from v2.6.
import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { getFirm, updateFirm, uploadFirmLogo, uploadFirmLetterhead, inviteTeamMember, listTeamMembers, removeTeamMember, updateTeamMember, getFirmActivityLog, getFeatureFlags, exportFirmData, deleteFirmAccount } from '../api/kailux'
import { clearOfflineSnapshots } from '../utils/offlineSnapshot'
import { Building2, Scale, Link2, Users, CreditCard, Lock } from 'lucide-react'
import ErrorMessage from '../components/ErrorMessage'
import CollapsibleSidebar from '../components/CollapsibleSidebar'

import Icon from '../components/Icon'

import { T, alpha } from '../theme/theme'
import ThemeToggle from '../components/ThemeToggle'
const TABS = [
  { id:'firm',         icon:Building2, label:'Firm Profile'        },
  { id:'compliance',   icon:Scale,     label:'Compliance Defaults' },
  { id:'integrations', icon:Link2,     label:'Integrations'        },
  { id:'team',         icon:Users,     label:'Team Members'        },
  { id:'subscription', icon:CreditCard,label:'Subscription'        },
  { id:'security',     icon:Lock,      label:'Security'            },
]

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December']

// Aug 2026 (kailux-fixes-log.md §17): Full IFRS / FRS 102 / US GAAP used to
// always render here, permanently disabled with a greyed "COMING SOON"
// badge — real clutter for a firm that will only ever see "IFRS for SMEs"
// as usable. Each entry's `flag` is now checked against live feature_flags
// (see the visibleStandards filter where STANDARDS is used) and the whole
// option simply doesn't render while its flag is off.
const STANDARDS = [
  { id:'IFRS for SMEs', label:'IFRS for SMEs', sub:'South African private companies — active', active:true  },
  { id:'Full IFRS',     label:'Full IFRS',     sub:'Listed companies & large corporates', active:true, flag:'framework_full_ifrs_enabled' },
  { id:'FRS 102',       label:'FRS 102',       sub:'UK clients', active:true, flag:'framework_frs102_enabled' },
  { id:'US GAAP',       label:'US GAAP',       sub:'American clients', active:true, flag:'framework_us_gaap_enabled' },
]

const INTEGRATIONS = [
  {
    id:'quickbooks', name:'QuickBooks Online', icon:<Icon name="plug" style={{ color:'#2CA01C' }} />,
    desc:'Intuit QuickBooks — the most common accounting software for small businesses in South Africa.',
    tags:['Excel','CSV'], phase2:true, phase2Label:'Direct connection coming',
    steps:[
      {n:1,text:'Open QuickBooks Online and go to <strong>Reports</strong> in the left sidebar.'},
      {n:2,text:'Search for <strong>"Trial Balance"</strong> in the search box and open the report.'},
      {n:3,text:'Set the <strong>Report period</strong> to your financial year end date.'},
      {n:4,text:'Click the <strong>Export</strong> button (top right) and choose <strong>Export to Excel (.xlsx)</strong>.'},
      {n:5,text:'Save the file to your desktop, then upload it to KaiLux using the button below.'},
    ],
  },
  {
    id:'xero', name:'Xero', icon:<Icon name="plug" style={{ color:'#13B5EA' }} />,
    desc:'Xero is widely used by South African accounting firms — exports a clean trial balance in seconds.',
    tags:['Excel','CSV'], phase2:true, phase2Label:'Direct connection coming',
    steps:[
      {n:1,text:'Open Xero and go to <strong>Accounting → Reports</strong>.'},
      {n:2,text:'Under <em>Financial</em>, click <strong>Trial Balance</strong>.'},
      {n:3,text:'Set the <strong>Date</strong> to your year end and click <strong>Update</strong>.'},
      {n:4,text:'Click the <strong>Export</strong> button and choose <strong>Excel (.xlsx)</strong>.'},
      {n:5,text:'Upload the downloaded file to KaiLux using the button below.'},
    ],
  },
  {
    id:'sage_cloud', name:'Sage Business Cloud', icon:<Icon name="plug" style={{ color:'#8B5CF6' }} />,
    desc:'Sage Business Cloud Accounting — the most common SA cloud accounting platform.',
    tags:['Excel','CSV'], phase2:false,
    steps:[
      {n:1,text:'Log into Sage Business Cloud and click <strong>Reporting</strong> in the top menu.'},
      {n:2,text:'Under <em>Financial Reports</em>, select <strong>Trial Balance</strong>.'},
      {n:3,text:'Set the date range to your financial year.'},
      {n:4,text:'Click <strong>Export to Excel</strong> or <strong>Export to CSV</strong>.'},
      {n:5,text:'Upload the downloaded file to KaiLux using the button below.'},
    ],
  },
]

function Input({ label, value, onChange, placeholder, type='text', hint, optional=false }) {
  return (
    <div style={{ marginBottom:'14px' }}>
      {label && (
        <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
          color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
          {label.toUpperCase()}
          {optional && <span style={{ color:T.mt, fontWeight:'400', marginLeft:'6px', fontSize:'9px' }}>(optional)</span>}
        </label>
      )}
      <input type={type} value={value || ''} onChange={e => onChange?.(e.target.value)}
        placeholder={placeholder}
        style={{ width:'100%', padding:'11px 13px', background:T.s3,
          border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
          fontSize:'14px', fontFamily:"'Outfit',sans-serif",
          outline:'none', boxSizing:'border-box', transition:'border 0.2s' }}
        onFocus={e => e.target.style.borderColor = T.or}
        onBlur={e  => e.target.style.borderColor = T.bd}/>
      {hint && <div style={{ fontSize:'10px', color:T.mt, marginTop:'4px' }}>{hint}</div>}
    </div>
  )
}

function Toggle({ label, sub, value, onChange }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
      padding:'14px 0', borderBottom:`1px solid ${T.bd}` }}>
      <div style={{ flex:1, paddingRight:'20px' }}>
        <div style={{ fontSize:'14px', fontWeight:'600', marginBottom:'3px' }}>{label}</div>
        {sub && <div style={{ fontSize:'12px', color:T.mt, lineHeight:'1.5' }}>{sub}</div>}
      </div>
      <div onClick={() => onChange?.(!value)}
        style={{ width:'44px', height:'24px', borderRadius:'999px', cursor:'pointer',
          background:value ? T.or : T.dm, position:'relative', transition:'background 0.3s',
          flexShrink:0 }}>
        <div style={{ position:'absolute', top:'3px',
          left:value ? '23px' : '3px', width:'18px', height:'18px',
          borderRadius:'50%', background:'white',
          transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.3)' }}/>
      </div>
    </div>
  )
}

function SaveBtn({ onClick, saved, loading }) {
  return (
    <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'8px' }}>
      <button onClick={onClick} disabled={loading}
        style={{ padding:'11px 24px', borderRadius:'8px', border:'none',
          cursor: loading ? 'not-allowed' : 'pointer',
          fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700',
          background: saved ? T.gnd : T.prg,
          color: saved ? T.gn : T.onpri, transition:'all 0.3s',
          boxShadow: saved ? 'none' : `0 4px 16px ${alpha(T.pri, 0.3)}`,
          opacity: loading ? 0.6 : 1 }}>
        {loading ? 'Saving...' : saved ? <>Saved <Icon name="check" /></> : 'Save Changes'}
      </button>
    </div>
  )
}

// ── LOGO UPLOAD ───────────────────────────────────────────────────────────────
function LogoUploader({ firmId, currentLogoUrl, onLogoSaved }) {
  const [uploading,  setUploading]  = useState(false)
  const [dragOver,   setDragOver]   = useState(false)
  const [error,      setError]      = useState('')
  const [previewUrl, setPreviewUrl] = useState(currentLogoUrl || '')
  const fileInputRef = useRef(null)

  const handleFile = async (file) => {
    if (!file) return
    const ext = file.name.split('.').pop().toLowerCase()
    if (!['png', 'jpg', 'jpeg'].includes(ext)) { setError('Please upload a PNG or JPG file.'); return }
    if (file.size > 2 * 1024 * 1024) { setError('Logo must be under 2MB.'); return }
    setError(''); setUploading(true)
    const localUrl = URL.createObjectURL(file); setPreviewUrl(localUrl)
    try {
      const { data } = await uploadFirmLogo(firmId, file)
      const savedUrl = data.url || data.logo_url || ''
      setPreviewUrl(savedUrl); onLogoSaved(savedUrl)
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload failed. Please try again.')
      setPreviewUrl(currentLogoUrl || '')
    } finally { setUploading(false) }
  }

  return (
    <div>
      <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
        color:T.lt, letterSpacing:'1px', marginBottom:'10px' }}>FIRM LOGO</label>
      <input ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg"
        style={{ display:'none' }}
        onChange={e => { const f = e.target.files[0]; if (f) handleFile(f) }}/>
      <div style={{ display:'flex', gap:'16px', alignItems:'flex-start', flexWrap:'wrap' }}>
        <div style={{ width:'120px', height:'72px', borderRadius:'8px',
          background:T.s3, border:`1.5px solid ${dragOver ? T.or : T.bd}`,
          display:'flex', alignItems:'center', justifyContent:'center',
          overflow:'hidden', flexShrink:0, transition:'border 0.2s', position:'relative' }}>
          {previewUrl ? (
            <img src={previewUrl} alt="Firm logo"
              style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain', padding:'8px' }}/>
          ) : (
            <div style={{ textAlign:'center', padding:'8px' }}>
              <div style={{ marginBottom:'4px', opacity:0.3, display:'flex', justifyContent:'center' }}><Building2 size={20} strokeWidth={1.5}/></div>
              <div style={{ fontSize:'9px', color:T.mt }}>No logo</div>
            </div>
          )}
          {uploading && (
            <div style={{ position:'absolute', inset:0, background:'rgba(8,9,22,0.75)',
              display:'flex', alignItems:'center', justifyContent:'center', borderRadius:'8px' }}>
              <div style={{ width:'20px', height:'20px', border:`2px solid ${alpha(T.or, 0.3)}`,
                borderTopColor:T.or, borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
            </div>
          )}
        </div>
        <div style={{ flex:1, minWidth:'180px' }}>
          <div onClick={() => !uploading && fileInputRef.current?.click()}
            onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f) }}
            onDragOver={e => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            style={{ padding:'14px 16px', borderRadius:'8px', cursor:'pointer',
              border:`1.5px dashed ${dragOver ? T.or : T.bd}`,
              background: dragOver ? T.ord : T.s2, textAlign:'center',
              transition:'all 0.2s', marginBottom:'8px' }}>
            <div style={{ fontSize:'14px', fontWeight:'600', color:T.mt, marginBottom:'3px' }}>
              {uploading ? 'Uploading...' : 'Drop logo here or click to browse'}
            </div>
            <div style={{ fontSize:'12px', color:T.mt }}>PNG or JPG · max 2MB</div>
          </div>
          {previewUrl && !uploading && (
            <button onClick={() => { setPreviewUrl(''); onLogoSaved('') }}
              style={{ padding:'6px 14px', borderRadius:'6px',
                border:`1px solid ${alpha(T.rd, 0.3)}`, background:alpha(T.rd, 0.06),
                color:T.rd, fontFamily:"'Outfit',sans-serif", fontSize:'12px',
                fontWeight:'600', cursor:'pointer' }}>
              Remove logo
            </button>
          )}
        </div>
      </div>
      {error && <ErrorMessage style={{ marginTop:'10px' }}>{error}</ErrorMessage>}
    </div>
  )
}


// ══════════════════════════════════════════════════════════════════════════════
// MAIN SETTINGS COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
function LetterheadUploader({ firmId, currentLetterheadUrl, onLetterheadSaved }) {
  const [uploading,  setUploading]  = useState(false)
  const [dragOver,   setDragOver]   = useState(false)
  const [error,      setError]      = useState('')
  const [previewUrl, setPreviewUrl] = useState(currentLetterheadUrl || '')
  const fileInputRef = useRef(null)
  const ACCEPTED_EXTS = ['docx', 'pdf', 'png', 'jpg', 'jpeg']

  const handleFile = async (file) => {
    if (!file) return
    const ext = file.name.split('.').pop().toLowerCase()
    if (!ACCEPTED_EXTS.includes(ext)) {
      setError('Please upload a Word document (.docx), PDF, or image (.png/.jpg).')
      return
    }
    if (file.size > 10 * 1024 * 1024) { setError('Letterhead must be under 10 MB.'); return }
    setError(''); setUploading(true)
    // A .docx/.pdf can't be rendered directly in an <img>, so we keep showing
    // the previous preview (if any) until the backend returns the rendered image.
    try {
      const { data } = await uploadFirmLetterhead(firmId, file)
      const savedUrl = data.url || ''
      setPreviewUrl(savedUrl); onLetterheadSaved(savedUrl)
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload failed. Please try again.')
      setPreviewUrl(currentLetterheadUrl || '')
    } finally { setUploading(false) }
  }

  return (
    <div>
      <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
        color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>FIRM LETTERHEAD</label>
      <p style={{ fontSize:'12px', color:T.mt, marginBottom:'10px', lineHeight:'1.5' }}>
        Upload your firm's official letterhead — Word document (.docx), PDF, or image
        (.png/.jpg). We use it exactly as uploaded, full page, as the background of the
        accountant's / auditor's letter in every AFS you generate, with the letter text
        composed directly onto it — nothing is extracted or redrawn.
      </p>
      <input ref={fileInputRef} type="file" accept=".docx,.pdf,.png,.jpg,.jpeg"
        style={{ display:'none' }} onChange={e => handleFile(e.target.files?.[0])}/>
      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]) }}
        style={{
          border:`2px dashed ${dragOver ? T.or : T.bd}`,
          borderRadius:'10px', padding:'18px', textAlign:'center',
          cursor:'pointer', background: dragOver ? T.ord : T.s2,
          transition:'all 0.2s', marginBottom:'10px',
        }}>
        {previewUrl ? (
          <div>
            <img src={previewUrl} alt="Letterhead preview"
              style={{ maxWidth:'100%', maxHeight:'160px', objectFit:'contain',
                borderRadius:'6px', marginBottom:'8px' }}/>
            <div style={{ fontSize:'11px', color:T.mt }}>
              {uploading ? 'Uploading and rendering letterhead…' : 'Click or drag to replace'}
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize:'28px', marginBottom:'6px' }}><Icon name="print" strokeWidth={1.5} style={{ color: T.mt }} /></div>
            <div style={{ fontSize:'13px', color:T.tx, fontWeight:'600', marginBottom:'3px' }}>
              {uploading ? 'Uploading and rendering letterhead…' : 'Upload letterhead'}
            </div>
            <div style={{ fontSize:'11px', color:T.mt }}>.DOCX · .PDF · .PNG · .JPG · max 10 MB</div>
          </div>
        )}
      </div>
      {error && <div style={{ fontSize:'12px', color:T.rd, marginTop:'6px' }}>{error}</div>}
    </div>
  )
}
export default function Settings() {
  const location = useLocation()
  const navigate = useNavigate()
  const { firm, logout, refreshFirm } = useAuth()

  const [tab,     setTab]     = useState(location.state?.tab || 'firm')
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700)
  const [showSidebar, setShowSidebar] = useState(false)
  const [saved,   setSaved]   = useState(false)
  const [saving,  setSaving]  = useState(false)
  const [saveErr, setSaveErr] = useState('')
  const [loaded,  setLoaded]  = useState(false)

  // Firm profile state
  const [firmName,          setFirmName]          = useState('')
  const [firmEmail,         setFirmEmail]         = useState('')
  const [firmPhone,         setFirmPhone]         = useState('')
  const [firmAddr,          setFirmAddr]          = useState('')
  const [firmReg,           setFirmReg]           = useState('')
  const [practiceNumber,    setPracticeNumber]    = useState('')
  const [individualMemberNumber, setIndividualMemberNumber] = useState('')
  const [responsiblePerson, setResponsiblePerson] = useState('')
  const [regulatoryBody,    setRegulatoryBody]    = useState('SAICA')
  const [firmLogoUrl,       setFirmLogoUrl]       = useState('')
  const [firmLetterheadUrl, setFirmLetterheadUrl] = useState('')

  // Compliance state
  const [standard,      setStandard]      = useState('IFRS for SMEs')
  const [engagement,    setEngagement]    = useState('compilation')
  const [yearEndMon,    setYearEndMon]    = useState('February')
  const [autoNotes,     setAutoNotes]     = useState(true)
  const [autoMapping,   setAutoMapping]   = useState(true)
  const [learnMappings, setLearnMappings] = useState(true)
  // Aug 2026 (kailux-fixes-log.md §17): Full IFRS/FRS 102/US GAAP and
  // Review/Audit only render at all once their flag is on — see
  // visibleStandards/visibleEngagementTypes below.
  const [flags,          setFlags]        = useState({})

  useEffect(() => { getFeatureFlags().then(setFlags).catch(() => setFlags({})) }, [])
  const visibleStandards = STANDARDS.filter(s => !s.flag || flags[s.flag])
  const ENGAGEMENT_TYPES = [
    { id:'compilation', label:'Compilation Engagement', sub:'ISRS 4410 — most common for private companies' },
    { id:'review',      label:'Independent Review',     sub:'ISRE 2400 — limited assurance', flag:'engagement_type_review_enabled' },
    { id:'audit',       label:'Full Audit',             sub:'ISA Standards — reasonable assurance', flag:'engagement_type_audit_enabled' },
    // Kept in sync with Signup.jsx's ENGAGEMENTS list — see its comment.
    { id:'accounting_officer', label:"Accounting Officer's Report", sub:'Close Corporations Act — for CC clients' },
    { id:'in_house',    label:'Prepared in-house', sub:'Prepared by your own staff, no external accountant. Without an audit or review this is only lawful for an owner-managed company with a Public Interest Score below 100' },
  ]
  const visibleEngagementTypes = ENGAGEMENT_TYPES.filter(e => !e.flag || flags[e.flag])
  const [requireAfsReview, setRequireAfsReview] = useState(false)
  const [reviewSettingErr, setReviewSettingErr] = useState('')

  // Security state
  const [twoFA,    setTwoFA]    = useState(false)

  // Data export (Settings → Firm Profile). Emailed, not downloaded — see
  // POST /api/firms/{firm_id}/export-data. A 403 just means the current
  // team member isn't an admin, handled as its own state like the
  // activity-log's activityForbidden above, not lumped in with real errors.
  const [exportSending,   setExportSending]   = useState(false)
  const [exportSentTo,    setExportSentTo]    = useState('')
  const [exportForbidden, setExportForbidden] = useState(false)
  const [exportError,     setExportError]     = useState('')

  // Account deletion (Settings → Firm Profile). Type-to-confirm modal so
  // nobody deletes an account by accident — the backend also checks the
  // exact phrase server-side rather than trusting the frontend alone.
  const [deleteModalOpen,   setDeleteModalOpen]   = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleting,          setDeleting]          = useState(false)
  const [deleteError,       setDeleteError]       = useState('')
  const [deleteForbidden,   setDeleteForbidden]   = useState(false)

  // Firm-wide activity log (real implementation — see _log_firm_activity /
  // GET /api/firms/{firm_id}/activity-log on the backend). Admin-only; a
  // 403 here just means the current team member isn't an admin, not an
  // error, so it's handled as its own state rather than lumped with
  // network failures.
  const [activityEntries,    setActivityEntries]    = useState(null)
  const [activityLoading,    setActivityLoading]    = useState(false)
  const [activityError,      setActivityError]      = useState('')
  const [activityForbidden,  setActivityForbidden]  = useState(false)
  const [activityOpen,       setActivityOpen]       = useState(false)
  const [activityExpandedId, setActivityExpandedId] = useState(null)

  // Team state
  const [inviteEmail,   setInviteEmail]   = useState('')
  const [inviteSent,    setInviteSent]    = useState(false)
  const [inviteSending, setInviteSending] = useState(false)
  const [inviteError,   setInviteError]   = useState('')
  const [members,       setMembers]       = useState([])
  const [seatsPurchased,setSeatsPurchased]= useState(1)
  const [membersLoading,setMembersLoading]= useState(false)
  const [removingId,    setRemovingId]    = useState(null)
  const [billingFrequency, setBillingFrequency] = useState('monthly')
  const [memberNotice, setMemberNotice] = useState(null) // { type:'ok'|'error', text }

  // ── Phase A2: PWA install state ───────────────────────────────────────────
  const [pwaInstallable,  setPwaInstallable]  = useState(false)
  const [pwaInstalled,    setPwaInstalled]    = useState(false)
  const [pwaInstalling,   setPwaInstalling]   = useState(false)

  useEffect(() => {
    // Check if install prompt already captured
    if (window.__pwaInstallPrompt) setPwaInstallable(true)

    // Listen for future prompt event
    const onPrompt = () => setPwaInstallable(true)
    const onInstalled = () => { setPwaInstalled(true); setPwaInstallable(false) }
    window.addEventListener('kailux:installprompt', onPrompt)
    window.addEventListener('kailux:appinstalled',  onInstalled)

    // Check if already running as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setPwaInstalled(true)
    }

    return () => {
      window.removeEventListener('kailux:installprompt', onPrompt)
      window.removeEventListener('kailux:appinstalled',  onInstalled)
    }
  }, [])

  const handlePwaInstall = async () => {
    const prompt = window.__pwaInstallPrompt
    if (!prompt) return
    setPwaInstalling(true)
    try {
      await prompt.prompt()
      const { outcome } = await prompt.userChoice
      if (outcome === 'accepted') {
        setPwaInstalled(true)
        setPwaInstallable(false)
        window.__pwaInstallPrompt = null
      }
    } catch (e) {
      console.warn('PWA install failed:', e)
    } finally {
      setPwaInstalling(false)
    }
  }
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 700)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const loadMembers = () => {
    if (!firm?.id) return
    setMembersLoading(true)
    listTeamMembers(firm.id)
      .then(d => { setMembers(d.members || []); setSeatsPurchased(d.seats_purchased || 1); setBillingFrequency(d.billing_frequency || 'monthly'); setMembersLoading(false) })
      .catch(() => setMembersLoading(false))
  }

  useEffect(() => {
    if (tab === 'team') loadMembers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, firm?.id])

  useEffect(() => {
    if (tab !== 'security' || !firm?.id) return
    setActivityLoading(true)
    setActivityError('')
    setActivityForbidden(false)
    getFirmActivityLog(firm.id)
      .then((data) => setActivityEntries(data.entries || []))
      .catch((err) => {
        if (err?.response?.status === 403) setActivityForbidden(true)
        else setActivityError('Could not load the activity log.')
      })
      .finally(() => setActivityLoading(false))
  }, [tab, firm?.id])

  const handleExportData = async () => {
    setExportSending(true)
    setExportError('')
    setExportForbidden(false)
    try {
      const data = await exportFirmData(firm.id)
      setExportSentTo(data.email || firmEmail || '')
    } catch (err) {
      if (err?.response?.status === 403) setExportForbidden(true)
      else setExportError(err?.response?.data?.detail || 'Could not prepare the export. Please try again.')
    } finally {
      setExportSending(false)
    }
  }

  const handleDeleteAccount = async () => {
    setDeleting(true)
    setDeleteError('')
    setDeleteForbidden(false)
    try {
      await deleteFirmAccount(firm.id, deleteConfirmText)
      clearOfflineSnapshots()
      await logout()
      try { sessionStorage.setItem('kailux_account_deleted', '1') } catch { /* ignore */ }
      navigate('/login')
    } catch (err) {
      if (err?.response?.status === 403) setDeleteForbidden(true)
      else setDeleteError(err?.response?.data?.detail || 'Could not delete the account. Please try again.')
      setDeleting(false)
    }
  }

  const handleRemoveMember = async (m) => {
    // Removing a member also lowers the billed seats (monthly: from the next invoice,
    // annual: re-set to team size at renewal), so say exactly what will happen first.
    const inUse = members.length
    const nextSeats = Math.min(seatsPurchased, Math.max(inUse - 1, seatsPurchased - 1, 1))
    const billingLine = billingFrequency === 'annual'
      ? 'Your plan is billed annually, so your seats will be re-set to your team size when it renews.'
      : nextSeats < seatsPurchased
        ? `Your billed seats will drop from ${seatsPurchased} to ${nextSeats} from your next invoice.`
        : 'Your billed seats are already at the minimum.'
    if (!window.confirm(`Remove ${m.email}?\n\nThey will lose access to KaiLux immediately. ${billingLine}`)) return
    setMemberNotice(null)
    setRemovingId(m.id)
    try {
      const res = await removeTeamMember(firm.id, m.id)
      if (res?.seat_update_failed) {
        setMemberNotice({ type:'error', text:`Removed ${m.email}, but we couldn't update your billed seats automatically. Please email support@kailux.co.za so you aren't charged for this seat.` })
      } else if (res?.seats_effective === 'next_invoice') {
        setMemberNotice({ type:'ok', text:`Removed ${m.email}. Your billed seats are now ${res.seats_after}, from your next invoice.` })
      } else if (res?.seats_effective === 'renewal') {
        setMemberNotice({ type:'ok', text:`Removed ${m.email}. Your seats will be re-set to your team size when your annual plan renews.` })
      } else {
        setMemberNotice({ type:'ok', text:`Removed ${m.email}.` })
      }
      loadMembers()
      // The Subscription tab reads seats/pool from the session's firm record: refresh it.
      refreshFirm?.()
    }
    catch (err) { setMemberNotice({ type:'error', text: err?.response?.data?.detail || 'Could not remove member.' }) }
    finally { setRemovingId(null) }
  }

  const handleToggleCanReview = async (memberId, newVal) => {
    try { await updateTeamMember(firm.id, memberId, { can_review_afs: newVal }); loadMembers() }
    catch (err) { setInviteError(err?.response?.data?.detail || 'Could not update permission.') }
  }

  useEffect(() => {
    if (!firm?.id) return
    getFirm(firm.id)
      .then(({ data }) => {
        setFirmName(data.name || '')
        setFirmEmail(data.email || '')
        setFirmPhone(data.firm_phone || '')
        setFirmAddr(data.firm_address || '')
        setFirmReg(data.reg_number || '')
        setPracticeNumber(data.practice_number || '')
        setIndividualMemberNumber(data.individual_member_number || '')
        setResponsiblePerson(data.responsible_person || '')
        setRegulatoryBody(data.regulatory_body || 'SAICA')
        setEngagement(data.engagement_type || 'compilation')
        setYearEndMon(data.year_end_month || 'February')
        setStandard(data.standard || 'IFRS for SMEs')
        setAutoNotes(data.auto_notes !== false)
        setAutoMapping(data.auto_mapping !== false)
        setLearnMappings(data.learn_mappings !== false)
        setRequireAfsReview(data.require_afs_review === true)
        setFirmLogoUrl(data.firm_logo_url || '')
        setFirmLetterheadUrl(data.firm_letterhead_url || '')
        setLoaded(true)
      })
      .catch(() => {
        setFirmName(firm.name || '')
        setFirmEmail(firm.email || '')
        setEngagement(firm.engagement_type || 'compilation')
        setLoaded(true)
      })
  }, [firm?.id])

  const doSave = async (payload) => {
    if (!firm?.id) return
    setSaving(true); setSaveErr('')
    try {
      await updateFirm(firm.id, payload)
      setSaved(true)
      setTimeout(() => setSaved(false), 2200)
    } catch (err) {
      const detail = err?.response?.data?.detail || ''
      setSaveErr(detail ? `Save failed: ${detail}` : 'Failed to save. Please try again.')
    } finally { setSaving(false) }
  }

  const saveFirmProfile = () => doSave({
    name: firmName.trim(), email: firmEmail.trim(),
    firm_phone: firmPhone.trim(), firm_address: firmAddr.trim(),
    reg_number: firmReg.trim(), practice_number: practiceNumber.trim(),
    individual_member_number: individualMemberNumber.trim(),
    responsible_person: responsiblePerson.trim(), regulatory_body: regulatoryBody,
  })

  const saveCompliance = () => doSave({
    standard, engagement_type: engagement, year_end_month: yearEndMon,
    auto_notes: autoNotes, auto_mapping: autoMapping, learn_mappings: learnMappings,
  })

  // Separate from saveCompliance/doSave above: this setting is admin-only
  // server-side (kailux backend returns 403 for non-admins), and saves
  // immediately on toggle rather than being batched with the other
  // compliance fields — bundling it in would mean a non-admin trying to
  // save any OTHER compliance setting also gets rejected, since the field
  // would be present in the payload either way.
  const handleToggleAfsReview = async (newVal) => {
    const previous = requireAfsReview
    setRequireAfsReview(newVal)
    setReviewSettingErr('')
    try {
      await updateFirm(firm.id, { require_afs_review: newVal })
    } catch (err) {
      setRequireAfsReview(previous)
      const detail = err?.response?.data?.detail || ''
      setReviewSettingErr(detail || 'Only a firm admin can change this setting.')
    }
  }

  // Phase A2: clear offline snapshots on sign-out
  const handleLogout = async () => {
    clearOfflineSnapshots()
    await logout()
    navigate('/login')
  }

  const card = (children) => (
    <div style={{ background:T.s1, border:`1px solid ${T.bd}`,
      borderRadius:'12px', padding:'20px', marginBottom:'14px' }}>
      {children}
    </div>
  )

  const sectionLabel = (text) => (
    <div style={{ fontSize:'12px', color:T.or, fontWeight:'700',
      letterSpacing:'1.5px', marginBottom:'14px' }}>{text}</div>
  )

  const sel = (label, value, onChange, options, optional=false) => (
    <div style={{ marginBottom:'14px' }}>
      <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
        color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
        {label.toUpperCase()}
        {optional && <span style={{ color:T.mt, fontWeight:'400', marginLeft:'6px', fontSize:'9px' }}>(optional)</span>}
      </label>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ width:'100%', padding:'11px 13px', background:T.s3,
          border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
          fontSize:'14px', fontFamily:"'Outfit',sans-serif",
          outline:'none', boxSizing:'border-box' }}>
        {options.map(o => <option key={o} value={o} style={{ background:T.s2 }}>{o}</option>)}
      </select>
    </div>
  )

  if (!loaded) return (
    <div style={{ minHeight:'100vh', background:T.bg, display:'flex',
      alignItems:'center', justifyContent:'center', color:T.mt,
      fontFamily:"'Outfit',sans-serif", fontSize:'14px' }}>
      Loading settings...
    </div>
  )

  // Shared brand/nav/footer content — rendered inside a plain div for the
  // mobile full-screen overlay, and inside CollapsibleSidebar for desktop
  // (which adds the collapse tab + floating reopen arrow).
  const sidebarNavContent = (
    <>
      <div style={{ padding:'20px 18px 16px', borderBottom:`1px solid ${T.bd}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          <div style={{ width:'32px', height:'32px', borderRadius:'8px',
            background:T.prg,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontFamily:"'Syne',sans-serif", fontWeight:'800', color:T.onpri, fontSize:'14px' }}>K</div>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'16px', fontWeight:'800' }}>
            Kai<span style={{color:T.or}}>Lux</span>
          </div>
        </div>
        <div style={{ fontSize:'10px', color:T.mt, marginTop:'5px', letterSpacing:'1px' }}>SETTINGS</div>
      </div>
      <nav style={{ padding:'8px', flex:1, overflowY:'auto' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setSaveErr('') }}
            style={{ display:'flex', alignItems:'center', gap:'8px', width:'100%',
              padding:'9px 12px', borderRadius:'8px', border:'none', cursor:'pointer',
              fontFamily:"'Outfit',sans-serif",
              background: tab===t.id ? T.ord : 'transparent',
              color: tab===t.id ? T.or : T.mt,
              fontSize:'13px', fontWeight: tab===t.id ? '700' : '500',
              borderLeft: tab===t.id ? `2px solid ${T.or}` : '2px solid transparent',
              marginBottom:'2px', transition:'all 0.15s' }}>
            <t.icon size={15} strokeWidth={2} style={{display:'inline', verticalAlign:'-2px', marginRight:'6px'}} />{t.label}
          </button>
        ))}
      </nav>
      <div style={{ padding:'14px 18px', borderTop:`1px solid ${T.bd}` }}>
        <ThemeToggle />
        <button onClick={() => navigate('/dashboard')}
          style={{ display:'block', width:'100%', padding:'8px', borderRadius:'8px',
            border:`1px solid ${T.bd}`, background:'transparent', color:T.mt,
            fontFamily:"'Outfit',sans-serif", fontSize:'13px', cursor:'pointer', marginBottom:'8px' }}>
          ← Dashboard
        </button>
        <button onClick={handleLogout}
          style={{ display:'block', width:'100%', padding:'8px', borderRadius:'8px',
            border:`1px solid ${alpha(T.rd, 0.3)}`, background:T.rdd, color:T.rd,
            fontFamily:"'Outfit',sans-serif", fontSize:'13px', cursor:'pointer' }}>
          Sign Out
        </button>
      </div>
    </>
  )

  return (
    <div style={{ minHeight:'100vh', background:T.bg,
      fontFamily:"'Outfit',sans-serif", color:T.tx, display:'flex' }}>

      {/* ── MOBILE TOP BAR ── */}
      {isMobile && (
        <div style={{ position:'fixed', top:0, left:0, right:0, zIndex:100,
          background:T.s1, borderBottom:`1px solid ${T.bd}`,
          padding:'0 16px', height:'56px',
          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'16px', fontWeight:'800' }}>
            Kai<span style={{color:T.or}}>Lux</span>
          </div>
          <button onClick={() => setShowSidebar(v => !v)}
            style={{ background:'none', border:`1px solid ${T.bd}`, borderRadius:'8px',
              color:T.tx, fontSize:'18px', padding:'4px 10px', cursor:'pointer' }}>
            {showSidebar ? <Icon name="x" /> : <Icon name="menu" />}
          </button>
        </div>
      )}

      {/* ── SIDEBAR ── */}
      {isMobile ? (
        <div style={{
          width:'100%', background:T.s1, borderRight:'none',
          display: showSidebar ? 'flex' : 'none',
          flexDirection:'column', flexShrink:0,
          position:'fixed', top:'56px', bottom:0, left:0, right:0,
          zIndex:99, height:'auto', overflowY:'auto'
        }}>
          {sidebarNavContent}
        </div>
      ) : (
        <CollapsibleSidebar width="220px" bg={T.s1} borderColor={T.bd}
          accentFrom={T.or} accentTo={T.or2} mutedColor={T.mt}
          position="sticky" top={0} height="100vh"
          storageKey="kailux:settingsSidebarCollapsed" ariaLabel="settings navigation">
          <div style={{ background:T.s1, borderRight:`1px solid ${T.bd}`,
            display:'flex', flexDirection:'column', height:'100vh', overflowY:'auto' }}>
            {sidebarNavContent}
          </div>
        </CollapsibleSidebar>
      )}

      {/* ── CONTENT ── */}
      <div style={{ flex:1, overflowY:'auto', maxHeight:'100vh',
        padding: isMobile ? '72px 16px 32px' : '32px 36px' }}>
        <div style={{ maxWidth:'700px' }}>

          {saveErr && (
            <div style={{ background:T.rdd, border:`1px solid ${alpha(T.rd, 0.3)}`,
              borderRadius:'8px', padding:'11px 16px', marginBottom:'16px',
              fontSize:'14px', color:T.rd }}>{saveErr}</div>
          )}

          {/* ── FIRM PROFILE ── */}
          {tab==='firm' && (
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px', fontWeight:'800', marginBottom:'4px' }}>Firm Profile</div>
              <div style={{ fontSize:'14px', fontWeight:'400', color:T.mt, marginBottom:'24px' }}>These details appear on every AFS document you generate</div>
              {card(<>
                {sectionLabel('FIRM LOGO')}
                <p style={{ fontSize:'12px', color:T.mt, marginBottom:'10px', lineHeight:'1.5' }}>
                  Optional. If you have a full letterhead image, use the <strong style={{color:T.tx}}>Firm Letterhead</strong> card
                  below instead — that gives a more professional result on the accountant's letter.
                  The logo is used as a fallback when no letterhead is uploaded.
                </p>
                <LogoUploader firmId={firm?.id} currentLogoUrl={firmLogoUrl}
                  onLogoSaved={(url) => {
                    setFirmLogoUrl(url)
                    if (firm?.id) updateFirm(firm.id, { firm_logo_url: url }).catch(() => {})
                  }}/>
              </>)}
              {card(<>
                <LetterheadUploader firmId={firm?.id} currentLetterheadUrl={firmLetterheadUrl}
                  onLetterheadSaved={(url) => {
                    setFirmLetterheadUrl(url)
                    if (firm?.id) updateFirm(firm.id, { firm_letterhead_url: url }).catch(() => {})
                  }}/>
              </>)}
              {card(<>
                {sectionLabel('FIRM DETAILS')}
                <Input label="Accounting Firm Name" value={firmName} onChange={setFirmName}
                  placeholder="e.g. Ndlovu & Associates Inc."
                  hint="Registered firm name — appears on every AFS document"/>
                <Input label="CIPC Registration Number" value={firmReg} onChange={setFirmReg}
                  placeholder="e.g. 2019/441002/07" optional/>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                  <Input label="Work Email" type="email" value={firmEmail} onChange={setFirmEmail}
                    placeholder="admin@yourfirm.co.za"/>
                  <Input label="Phone Number" value={firmPhone} onChange={setFirmPhone}
                    placeholder="+27 15 XXX XXXX" optional/>
                </div>
                <Input label="Physical / Business Address" value={firmAddr} onChange={setFirmAddr}
                  placeholder="25 Erasmus Street, Polokwane, Limpopo, 0699"
                  hint="Appears on the accountant's compilation report" optional/>
              </>)}
              {card(<>
                {sectionLabel('PROFESSIONAL REGISTRATION')}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                  {/* Kept in sync with Signup.jsx's regulatory body list —
                      was missing CIBA/SAIBA, IAC, CGISA/ICSA and SAIGA even
                      though CIMA/ACCA were already here (Aug 2026). */}
                  {sel('Regulatory Body', regulatoryBody, setRegulatoryBody,
                    ['SAICA','SAIPA','IRBA','CIBA','IAC','CIMA','ACCA','CGISA','SAIGA','Other'])}
                  <Input label="Practice Number" value={practiceNumber} onChange={setPracticeNumber}
                    placeholder="e.g. 912026E" hint="Printed on every AFS" optional/>
                </div>
                <Input label="Individual Member Number" value={individualMemberNumber}
                  onChange={setIndividualMemberNumber} placeholder="e.g. 12345678"
                  hint="For signing in your own individual capacity — falls back onto the AFS practice-number line when no firm-level practice number is set" optional/>
                <Input label="Responsible Individual / Signing Partner"
                  value={responsiblePerson} onChange={setResponsiblePerson}
                  placeholder="e.g. T. Madembe CA(SA)"
                  hint="Signs the compilation report — appears in practitioner sign-off. For in-house preparation this is the preparer: enter their name and professional designation (e.g. Jane Smith, SAIPA) — it is shown on the first page of the AFS" optional/>
              </>)}
              <SaveBtn onClick={saveFirmProfile} saved={saved} loading={saving}/>

              {card(<>
                {sectionLabel('YOUR DATA')}
                <p style={{ fontSize:'13px', color:T.mt, lineHeight:'1.6', marginBottom:'14px' }}>
                  Get a copy of your firm profile, clients, AFS job history, and team
                  members. We'll email a secure download link to your registered
                  account email — it isn't shown or downloaded here directly.
                </p>
                {exportForbidden ? (
                  <div style={{ fontSize:'13px', color:T.mt, lineHeight:'1.6' }}>
                    Only a firm admin can export the firm's data.
                  </div>
                ) : exportSentTo ? (
                  <div style={{ fontSize:'13px', color:T.gn || T.tx, lineHeight:'1.6' }}>
                    <Icon name="check" /> Sent to <strong>{exportSentTo}</strong>. The link expires in 48 hours.
                  </div>
                ) : (
                  <>
                    {exportError && <ErrorMessage>{exportError}</ErrorMessage>}
                    <button onClick={handleExportData} disabled={exportSending}
                      style={{ padding:'10px 20px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                        background:T.s2 || 'transparent', color:T.tx, fontSize:'13px', fontWeight:'700',
                        cursor: exportSending ? 'not-allowed' : 'pointer', opacity: exportSending ? 0.6 : 1 }}>
                      {exportSending ? 'Preparing export…' : 'Email me my data'}
                    </button>
                  </>
                )}
              </>)}

              {card(<>
                {sectionLabel('DANGER ZONE')}
                <p style={{ fontSize:'13px', color:T.mt, lineHeight:'1.6', marginBottom:'14px' }}>
                  Permanently delete your firm's KaiLux account — clients, AFS
                  history, documents, and team members. This cannot be undone.
                  We recommend using "Email me my data" above first if you
                  want a copy.
                </p>
                {deleteForbidden ? (
                  <div style={{ fontSize:'13px', color:T.mt, lineHeight:'1.6' }}>
                    Only a firm admin can delete the firm's account.
                  </div>
                ) : (
                  <button onClick={() => setDeleteModalOpen(true)}
                    style={{ padding:'10px 20px', borderRadius:'8px', border:`1px solid ${T.rd}`,
                      background:'transparent', color:T.rd, fontSize:'13px', fontWeight:'700',
                      cursor:'pointer' }}>
                    Delete my account
                  </button>
                )}
              </>)}
            </div>
          )}

          {/* ── COMPLIANCE ── */}
          {tab==='compliance' && (
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px', fontWeight:'800', marginBottom:'4px' }}>Compliance Defaults</div>
              <div style={{ fontSize:'14px', fontWeight:'400', color:T.mt, marginBottom:'24px' }}>Applied to every new AFS. Can be overridden per client.</div>
              {card(<>
                {sectionLabel('ACCOUNTING STANDARD')}
                <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                  {visibleStandards.map(s => (
                    <label key={s.id} onClick={() => setStandard(s.id)}
                      style={{ display:'flex', gap:'12px', alignItems:'center',
                        padding:'12px 14px', borderRadius:'8px', cursor:'pointer',
                        background: standard===s.id ? T.ord : T.s2,
                        border:`1.5px solid ${standard===s.id ? T.or : T.bd}`,
                        transition:'all 0.2s' }}>
                      <input type="radio" name="standard" value={s.id}
                        checked={standard===s.id} onChange={() => setStandard(s.id)}
                        style={{ accentColor:T.or }}/>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:'14px', fontWeight:'700',
                          color: standard===s.id ? T.or : T.tx }}>{s.label}</div>
                        <div style={{ fontSize:'12px', color:T.mt, marginTop:'2px' }}>{s.sub}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </>)}
              {card(<>
                {sectionLabel('ENGAGEMENT TYPE')}
                {visibleEngagementTypes.map(e => (
                  <label key={e.id} style={{ display:'flex', gap:'12px', alignItems:'center',
                    padding:'12px 14px', borderRadius:'8px', cursor:'pointer',
                    background: engagement===e.id ? T.ord : T.s2,
                    border:`1.5px solid ${engagement===e.id ? T.or : T.bd}`,
                    marginBottom:'8px', transition:'all 0.2s' }}>
                    <input type="radio" name="eng" value={e.id}
                      checked={engagement===e.id}
                      onChange={() => setEngagement(e.id)}
                      style={{ accentColor:T.or }}/>
                    <div>
                      <div style={{ fontSize:'14px', fontWeight:'700',
                        color: engagement===e.id ? T.or : T.tx, display:'flex', alignItems:'center', gap:'8px' }}>
                        {e.label}
                      </div>
                      <div style={{ fontSize:'12px', color:T.mt, marginTop:'2px' }}>{e.sub}</div>
                    </div>
                  </label>
                ))}
              </>)}
              {card(<>
                {sectionLabel('DEFAULT YEAR END')}
                {sel('Default Financial Year End Month', yearEndMon, setYearEndMon, MONTHS)}
              </>)}
              {card(<>
                {sectionLabel('PROFESSIONAL SAFEGUARDS')}
                <Toggle label="Require internal review before sign-off"
                  sub="When ON — an AFS must be approved by a firm admin or designated
                       reviewer before it can be sent for director sign-off. Off by
                       default; this is entirely your firm's choice, not a KaiLux
                       requirement."
                  value={requireAfsReview} onChange={handleToggleAfsReview}/>
                {reviewSettingErr && (
                  <div style={{ fontSize:'12px', color:T.rd, marginTop:'6px' }}>
                    {reviewSettingErr}
                  </div>
                )}
              </>)}
              {card(<>
                {sectionLabel('AI BEHAVIOUR')}
                <Toggle label="Auto-generate notes"
                  sub="When ON — AI drafts all 22 mandatory notes automatically."
                  value={autoNotes} onChange={setAutoNotes}/>
                <Toggle label="Auto-map accounts on upload"
                  sub="When ON — AI maps every account immediately after upload."
                  value={autoMapping} onChange={setAutoMapping}/>
                <Toggle label="Learn from manual corrections"
                  sub="When ON — your manual mapping corrections are saved and reused."
                  value={learnMappings} onChange={setLearnMappings}/>
              </>)}
              <SaveBtn onClick={saveCompliance} saved={saved} loading={saving}/>
            </div>
          )}

          {/* ── INTEGRATIONS ── */}
          {tab==='integrations' && (
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px', fontWeight:'800', marginBottom:'4px' }}>Integrations</div>
              <div style={{ fontSize:'14px', fontWeight:'400', color:T.mt, marginBottom:'24px' }}>
                Export a trial balance from your accounting software, then upload it to KaiLux. Direct connections are coming in a future release.
              </div>
              {INTEGRATIONS.map(intg => (
                <div key={intg.id}>
                  {card(<>
                    <div style={{ display:'flex', alignItems:'flex-start', gap:'14px', marginBottom:'14px' }}>
                      <div style={{ fontSize:'26px', lineHeight:1 }}>{intg.icon}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
                          <div style={{ fontSize:'16px', fontWeight:'700', color:T.tx }}>{intg.name}</div>
                          {intg.phase2 && (
                            <span style={{ fontSize:'9px', fontWeight:'700', padding:'2px 8px',
                              borderRadius:'6px', background:T.pud, border:`1px solid ${alpha(T.pu, 0.3)}`,
                              color:T.pu, letterSpacing:'0.5px' }}>
                              {(intg.phase2Label || 'COMING SOON').toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize:'13px', color:T.mt, marginTop:'4px', lineHeight:'1.5' }}>{intg.desc}</div>
                        {intg.tags && intg.tags.length > 0 && (
                          <div style={{ display:'flex', gap:'6px', marginTop:'8px', flexWrap:'wrap' }}>
                            {intg.tags.map(tag => (
                              <span key={tag} style={{ fontSize:'10px', fontWeight:'600', padding:'3px 9px',
                                borderRadius:'999px', background:T.s3, border:`1px solid ${T.bd}`, color:T.lt }}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {intg.steps && intg.steps.length > 0 && (
                      <div style={{ borderTop:`1px solid ${T.bd}`, paddingTop:'14px' }}>
                        {sectionLabel('HOW TO EXPORT YOUR TRIAL BALANCE')}
                        <ol style={{ margin:0, paddingLeft:'20px' }}>
                          {intg.steps.map(step => (
                            <li key={step.n} style={{ fontSize:'13px', color:T.tx, lineHeight:'1.8' }}>
                              <span dangerouslySetInnerHTML={{ __html: step.text }} />
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </>)}
                </div>
              ))}
            </div>
          )}

          {/* ── TEAM ── */}
          {tab==='team' && (
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px', fontWeight:'800', marginBottom:'4px' }}>Team Members</div>
              <div style={{ fontSize:'14px', fontWeight:'400', color:T.mt, marginBottom:'24px' }}>Manage who has access to KaiLux</div>

              {card(<>
                {sectionLabel('SEATS')}
                <div style={{ fontSize:'14px', color:T.tx, marginBottom:'4px' }}>
                  {members.length} of {seatsPurchased} seat{seatsPurchased>1?'s':''} used
                  {members.length >= seatsPurchased && (
                    <span style={{ color:T.or, fontWeight:700 }}> — full</span>
                  )}
                </div>
                <div style={{ fontSize:'12px', color:T.mt }}>
                  Need more seats? <button onClick={() => navigate('/billing')}
                    style={{ background:'none', border:'none', color:T.or, cursor:'pointer',
                      padding:0, textDecoration:'underline', fontSize:'12px' }}>Update your plan</button>
                </div>
              </>)}

              {card(<>
                {sectionLabel('INVITE TEAM MEMBER')}
                <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:'8px' }}>
                  <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                    placeholder="colleague@firm.co.za"
                    style={{ padding:'11px 13px', background:T.s3, border:`1px solid ${T.bd}`,
                      borderRadius:'8px', color:T.tx, fontSize:'14px',
                      fontFamily:"'Outfit',sans-serif", outline:'none' }}
                    onFocus={e => e.target.style.borderColor = T.or}
                    onBlur={e  => e.target.style.borderColor = T.bd}/>
                  <button
                    onClick={async () => {
                      if (!inviteEmail.includes('@')) return
                      setInviteSending(true); setInviteError('')
                      try {
                        await inviteTeamMember(firm?.id, inviteEmail)
                        setInviteSent(true); setInviteEmail('')
                        loadMembers()
                        setTimeout(() => setInviteSent(false), 3000)
                      } catch (err) {
                        setInviteError(err?.response?.data?.detail?.message || err?.response?.data?.detail || 'Failed to send invitation. Please try again.')
                      } finally { setInviteSending(false) }
                    }}
                    disabled={inviteSending || members.length >= seatsPurchased}
                    style={{ padding:'11px 18px', borderRadius:'8px', border:'none',
                      background: inviteSent ? T.gnd : T.prg,
                      color: inviteSent ? T.gn : T.onpri, fontFamily:"'Outfit',sans-serif",
                      fontSize:'14px', fontWeight:'700',
                      cursor: (inviteSending || members.length >= seatsPurchased) ? 'not-allowed' : 'pointer',
                      opacity: (inviteSending || members.length >= seatsPurchased) ? 0.6 : 1,
                      whiteSpace:'nowrap' }}>
                    {inviteSending ? 'Sending...' : inviteSent ? <>Sent <Icon name="check" /></> : 'Send Invite'}
                  </button>
                </div>
              {inviteError && (
                <div style={{ marginTop:'10px', fontSize:'13px', color:T.rd,
                  background:T.rdd, padding:'8px 12px', borderRadius:'6px',
                  border:`1px solid ${alpha(T.rd, 0.2)}` }}><Icon name="alert" /> {inviteError}</div>
              )}
              </>)}

              {card(<>
                {sectionLabel('MEMBERS')}
                {memberNotice && (
                  <div role="status" style={{ marginBottom:'10px', fontSize:'13px',
                    color: memberNotice.type==='ok' ? T.gn : T.rd,
                    background: memberNotice.type==='ok' ? T.gnd : T.rdd,
                    padding:'8px 12px', borderRadius:'6px',
                    border:`1px solid ${alpha(memberNotice.type==='ok' ? T.gn : T.rd, 0.2)}` }}>
                    {memberNotice.text}
                  </div>
                )}
                {membersLoading ? (
                  <div style={{ fontSize:'13px', color:T.mt, padding:'12px 0' }}>Loading…</div>
                ) : members.length === 0 ? (
                  <div style={{ fontSize:'13px', color:T.mt, padding:'12px 0' }}>No team members yet.</div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:'2px' }}>
                    {members.map(m => (
                      <div key={m.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                        padding:'10px 0', borderBottom:`1px solid ${T.bd}` }}>
                        <div>
                          <div style={{ fontSize:'14px', color:T.tx, fontWeight:600 }}>{m.email}</div>
                          <div style={{ fontSize:'12px', color:T.mt, marginTop:'2px', textTransform:'capitalize' }}>
                            {m.role} · {m.status === 'invited' ? 'Invite pending' : 'Active'}
                            {m.role === 'admin' && ' · Can review AFS'}
                          </div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
                          {m.role !== 'admin' && (
                            <label style={{ display:'flex', alignItems:'center', gap:'6px',
                              fontSize:'12px', color:T.mt, cursor:'pointer' }}>
                              <input type="checkbox" checked={!!m.can_review_afs}
                                onChange={e => handleToggleCanReview(m.id, e.target.checked)}
                                style={{ accentColor:T.or }}/>
                              Can review AFS
                            </label>
                          )}
                          {!m.is_you && !m.is_owner && (
                            <button onClick={() => handleRemoveMember(m)} disabled={removingId===m.id}
                              title="Remove this person and stop paying for their seat"
                              style={{ background:T.rdd, border:`1px solid ${alpha(T.rd, 0.35)}`, color:T.rd,
                                cursor:'pointer', fontSize:'13px', fontWeight:700, padding:'6px 14px',
                                borderRadius:'6px', opacity: removingId===m.id ? 0.5 : 1 }}>
                              {removingId===m.id ? 'Removing…' : 'Remove'}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>)}
            </div>
          )}

          {/* ── SUBSCRIPTION ── */}
          {tab==='subscription' && (
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px', fontWeight:'800', marginBottom:'4px' }}>Subscription</div>
              <div style={{ fontSize:'14px', fontWeight:'400', color:T.mt, marginBottom:'24px' }}>Manage your KaiLux plan and billing</div>
              {card(<>
                {sectionLabel('CURRENT PLAN')}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
                  <div>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'20px', fontWeight:'800', marginBottom:'4px' }}>
                      {(firm?.user_count || 1)} seat{(firm?.user_count||1)>1?'s':''} · R{500*(firm?.user_count||1)}/month
                    </div>
                    <div style={{ fontSize:'14px', fontWeight:'700',
                      color: firm?.subscription_status==='unpaid' ? T.rd : T.or }}>
                      {firm?.subscription_status==='unpaid' ? 'No active subscription — AFS generation is paused' : `${firm?.afs_limit || 10} AFS/month pooled`}
                    </div>
                  </div>
                  <span style={{ fontSize:'10px', fontWeight:'700', padding:'4px 12px', borderRadius:'6px',
                    background: firm?.subscription_status==='unpaid' ? T.ord : T.gnd,
                    color: firm?.subscription_status==='unpaid' ? T.or : T.gn }}>
                    {firm?.subscription_status==='unpaid' ? 'UNPAID' : 'ACTIVE'}
                  </span>
                </div>
              </>)}
              <button onClick={() => navigate('/billing')}
                style={{ padding:'11px 20px', borderRadius:'8px', border:'none',
                  background:T.prg, color:T.onpri,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
                {firm?.subscription_status==='unpaid' ? 'Subscribe Now' : 'Change Plan'}
              </button>
            </div>
          )}

          {/* ── SECURITY ── */}
          {tab==='security' && (
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px', fontWeight:'800', marginBottom:'4px' }}>Security</div>
              <div style={{ fontSize:'14px', fontWeight:'400', color:T.mt, marginBottom:'24px' }}>Protect your firm's data and client information</div>

              {/* ── Phase A2: PWA Install Card ─────────────────────────────── */}
              {card(<>
                {sectionLabel('INSTALL KAILUX')}
                <div style={{ display:'flex', justifyContent:'space-between',
                  alignItems:'center', gap:'20px', flexWrap:'wrap' }}>
                  <div style={{ flex:1, minWidth:'220px' }}>
                    <div style={{ fontSize:'14px', fontWeight:'600', marginBottom:'6px' }}>
                      Install as a desktop or mobile app
                    </div>
                    <div style={{ fontSize:'13px', color:T.mt, lineHeight:'1.6' }}>
                      Install KaiLux on your device for faster access, offline browsing
                      of your client list, and a dedicated app experience — no browser
                      tab needed. Works on Chrome, Edge, and Safari (iOS).
                    </div>
                  </div>
                  <div style={{ flexShrink:0 }}>
                    {pwaInstalled ? (
                      <div style={{ display:'flex', alignItems:'center', gap:'8px',
                        padding:'10px 18px', borderRadius:'8px',
                        background:T.gnd, border:`1px solid ${alpha(T.gn, 0.3)}` }}>
                        <span style={{ fontSize:'16px' }}><Icon name="check" /></span>
                        <span style={{ fontSize:'14px', fontWeight:'700', color:T.gn }}>
                          Installed
                        </span>
                      </div>
                    ) : pwaInstallable ? (
                      <button
                        onClick={handlePwaInstall}
                        disabled={pwaInstalling}
                        style={{ padding:'10px 22px', borderRadius:'8px', border:'none',
                          background: pwaInstalling
                            ? T.mt
                            : T.prg,
                          color:T.onpri, fontFamily:"'Outfit',sans-serif",
                          fontSize:'14px', fontWeight:'700',
                          cursor: pwaInstalling ? 'not-allowed' : 'pointer',
                          display:'flex', alignItems:'center', gap:'8px' }}>
                        {pwaInstalling ? (
                          <>
                            <span style={{ width:'14px', height:'14px',
                              border:`2px solid ${alpha(T.onpri, 0.3)}`,
                              borderTopColor:T.onpri, borderRadius:'50%',
                              animation:'spin 0.8s linear infinite', display:'inline-block' }}/>
                            Installing...
                          </>
                        ) : <><Icon name="download" /> Install KaiLux</>}
                      </button>
                    ) : (
                      <div style={{ padding:'10px 18px', borderRadius:'8px',
                        background:T.s2, border:`1px solid ${T.bd}`,
                        fontSize:'13px', color:T.mt, lineHeight:'1.5',
                        maxWidth:'220px' }}>
                        Open KaiLux in Chrome or Edge on desktop, or Safari on iPhone,
                        to see the Install option.
                      </div>
                    )}
                  </div>
                </div>
              </>)}

              {card(<>
                {sectionLabel('ACCESS CONTROL')}
                <Toggle label="Two-Factor Authentication"
                  sub="Require all team members to use an authenticator app"
                  value={twoFA} onChange={setTwoFA}/>
              </>)}

              {card(<>
                {sectionLabel('FIRM ACTIVITY LOG')}
                {activityForbidden ? (
                  <div style={{ fontSize:'13px', color:T.mt, lineHeight:'1.6' }}>
                    Only a firm admin can view the firm-wide activity log.
                  </div>
                ) : activityLoading ? (
                  <div style={{ fontSize:'13px', color:T.mt }}>Loading activity log...</div>
                ) : activityError ? (
                  <ErrorMessage>{activityError}</ErrorMessage>
                ) : !activityEntries || activityEntries.length === 0 ? (
                  <div style={{ fontSize:'13px', color:T.mt, lineHeight:'1.6' }}>
                    No activity recorded yet. Team management, client changes, firm
                    settings updates, and every step of each AFS engagement (checklist
                    confirmations, review decisions, sign-off) will appear here as they happen.
                  </div>
                ) : (
                  <div>
                    <div onClick={() => setActivityOpen(o => !o)}
                      style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                        cursor:'pointer', marginBottom: activityOpen ? '14px' : 0 }}>
                      <div style={{ fontSize:'13px', fontWeight:'700', color:T.tx }}>
                        <Icon name="log" /> {activityEntries.length} recent event{activityEntries.length !== 1 ? 's' : ''}
                      </div>
                      <div style={{ fontSize:'12px', color:T.mt }}>{activityOpen ? '▲' : '▼'}</div>
                    </div>
                    {activityOpen && (
                      <div style={{ display:'flex', flexDirection:'column', gap:'10px',
                        maxHeight:'420px', overflowY:'auto' }}>
                        {activityEntries.map(entry => {
                          const hasDiff = entry.diff && Object.keys(entry.diff).length > 0
                          const context = entry.source === 'afs'
                            ? [entry.client_name, entry.financial_year].filter(Boolean).join(' — ')
                            : ''
                          const sourceIcon = entry.source === 'afs' ? <Icon name="file" />
                            : entry.source === 'account' ? <Icon name="building" /> : <Icon name="user" />
                          return (
                            <div key={`${entry.source}-${entry.id}`} style={{ fontSize:'12px', lineHeight:'1.6' }}>
                              <div onClick={() => hasDiff && setActivityExpandedId(id => id === entry.id ? null : entry.id)}
                                style={{ cursor: hasDiff ? 'pointer' : 'default' }}>
                                <span style={{ marginRight:'6px' }}>{sourceIcon}</span>
                                <span style={{ color:T.tx, fontWeight:'600' }}>{entry.summary}</span>
                                {context && <span style={{ color:T.mt }}>{' · '}{context}</span>}
                                <span style={{ color:T.mt }}>
                                  {' — '}{entry.actor_email || (entry.source === 'account' ? 'KaiLux' : 'unknown')}
                                  {', '}{new Date(entry.created_at).toLocaleString()}
                                </span>
                              </div>
                              {activityExpandedId === entry.id && hasDiff && (
                                <pre style={{ marginTop:'6px', padding:'10px 12px', background:T.s3,
                                  border:`1px solid ${T.bd}`, borderRadius:'7px', fontSize:'11px',
                                  overflow:'auto', color:T.lt }}>
                                  {JSON.stringify(entry.diff, null, 2)}
                                </pre>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>)}

              {card(<>
                {sectionLabel('CHANGE PASSWORD')}
                <Input label="Current Password" type="password" value="" onChange={() => {}} placeholder="Your current password"/>
                <Input label="New Password" type="password" value="" onChange={() => {}} placeholder="Minimum 8 characters"/>
                <Input label="Confirm New Password" type="password" value="" onChange={() => {}} placeholder="Repeat new password"/>
                <button style={{ padding:'11px 20px', borderRadius:'8px', border:'none',
                  background:T.prg, color:T.onpri,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
                  Update Password
                </button>
              </>)}
            </div>
          )}

        </div>
      </div>

      {deleteModalOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:200,
          display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
          <div style={{ width:'100%', maxWidth:'440px', background:T.s1, border:`1px solid ${T.bd}`,
            borderRadius:'14px', padding:'28px' }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'18px', fontWeight:'800',
              color:T.rd, marginBottom:'8px' }}>Delete your KaiLux account?</div>
            <p style={{ fontSize:'13px', color:T.mt, lineHeight:'1.6', marginBottom:'16px' }}>
              This permanently deletes your firm profile, clients, AFS history, documents,
              and team members. This cannot be undone. Type <strong style={{color:T.tx}}>DELETE MY ACCOUNT</strong> below to confirm.
            </p>
            {deleteError && <ErrorMessage>{deleteError}</ErrorMessage>}
            <input value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE MY ACCOUNT" autoFocus
              style={{ width:'100%', padding:'11px 14px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                background:T.s2, color:T.tx, fontSize:'14px', marginBottom:'18px', boxSizing:'border-box' }}/>
            <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
              <button onClick={() => { setDeleteModalOpen(false); setDeleteConfirmText(''); setDeleteError('') }}
                disabled={deleting}
                style={{ padding:'10px 18px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                  background:'transparent', color:T.tx, fontSize:'13px', fontWeight:'700', cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText.trim().toUpperCase() !== 'DELETE MY ACCOUNT'}
                style={{ padding:'10px 18px', borderRadius:'8px', border:'none',
                  background:T.rd, color:T.onpri, fontSize:'13px', fontWeight:'700',
                  cursor: (deleting || deleteConfirmText.trim().toUpperCase() !== 'DELETE MY ACCOUNT') ? 'not-allowed' : 'pointer',
                  opacity: (deleting || deleteConfirmText.trim().toUpperCase() !== 'DELETE MY ACCOUNT') ? 0.5 : 1 }}>
                {deleting ? 'Deleting…' : 'Permanently delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
